import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Mail, Send, SquareDashedBottomCode, Users, AlertTriangle,
  CheckCircle2, XCircle, RotateCcw, Trash2, ShieldOff,
} from 'lucide-react';
import {
  adminBatchAssignAndSend, adminBatchRetrySend, adminBatchSendTest,
  adminBatchVoidRemaining, adminDeletePartnerCodeBatch,
  adminGetPartnerCodeBatch, adminListBatchCodes, adminUpdatePartnerCodeBatch,
} from '@/lib/api';

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const AdminPartnerBatchDetail = () => {
  const { id } = useParams();
  const [batch, setBatch] = useState(null);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [composer, setComposer] = useState({ subject: '', body: '' });
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [recipients, setRecipients] = useState('');
  const [sending, setSending] = useState(false);
  const [sendReport, setSendReport] = useState(null);
  const [mismatchConfirm, setMismatchConfirm] = useState(null);
  const [retryingId, setRetryingId] = useState(null);
  const [voiding, setVoiding] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const dirtyRef = useRef(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([
        adminGetPartnerCodeBatch(id),
        adminListBatchCodes(id, statusFilter ? { status: statusFilter } : {}),
      ]);
      setBatch(b);
      setCodes(c.items || []);
      // Only initialise composer from server on first load (otherwise user edits get clobbered).
      if (!dirtyRef.current) {
        setComposer({ subject: b.email_subject || '', body: b.email_body || '' });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load batch.');
    } finally { setLoading(false); }
  }, [id, statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  const counts = batch?.counts || {};
  const unassignedCount = useMemo(
    () => codes.filter((c) => c.status === 'unused' && !c.assigned_email).length,
    [codes],
  );
  const parsedRecipients = useMemo(() => {
    if (!recipients) return { valid: [], invalid: [], duplicates: [] };
    const parts = recipients.split(/[\s,;]+/).map((p) => p.trim().toLowerCase()).filter(Boolean);
    const seen = new Set();
    const valid = []; const invalid = []; const duplicates = [];
    const re = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    for (const p of parts) {
      if (!re.test(p)) invalid.push(p);
      else if (seen.has(p)) duplicates.push(p);
      else { seen.add(p); valid.push(p); }
    }
    return { valid, invalid, duplicates };
  }, [recipients]);

  const saveComposer = async () => {
    if (!composer.subject.trim() || !composer.body.trim()) {
      toast.error('Subject and body are both required.');
      return;
    }
    setSaving(true);
    try {
      const updated = await adminUpdatePartnerCodeBatch(id, {
        email_subject: composer.subject,
        email_body: composer.body,
      });
      setBatch(updated);
      dirtyRef.current = false;
      toast.success('Email template saved.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save template.');
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!composer.subject.trim() || !composer.body.trim()) {
      toast.error('Fill in subject and body before testing.');
      return;
    }
    setTesting(true);
    try {
      const res = await adminBatchSendTest(id, {
        email: testEmail || undefined,
        subject: composer.subject,
        body: composer.body,
      });
      toast.success(`Test email sent to ${res.sent_to}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Test send failed.');
    } finally { setTesting(false); }
  };

  const doAssignAndSend = async (allow_mismatch = false) => {
    if (!recipients.trim()) {
      toast.error('Paste at least one recipient email.');
      return;
    }
    // Ensure the template is persisted first so sends use the latest copy.
    if (dirtyRef.current) await saveComposer();
    setSending(true);
    try {
      const res = await adminBatchAssignAndSend(id, {
        emails_raw: recipients,
        allow_mismatch,
      });
      setSendReport(res);
      setMismatchConfirm(null);
      setRecipients('');
      await refresh();
      toast.success(`Sent ${res.sent_ok}/${res.attempted} emails${res.failed ? ` (${res.failed} failed)` : ''}.`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d && typeof d === 'object' && d.mismatch) {
        setMismatchConfirm(d);
      } else {
        toast.error(typeof d === 'string' ? d : 'Send failed.');
      }
    } finally { setSending(false); }
  };

  const retry = async (codeId) => {
    setRetryingId(codeId);
    try {
      await adminBatchRetrySend(id, codeId);
      await refresh();
      toast.success('Retry succeeded.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Retry failed.');
    } finally { setRetryingId(null); }
  };

  const doVoidRemaining = async () => {
    if (!window.confirm(`Void all ${unassignedCount || counts.unused || 0} unused codes in this batch? Redeemed codes stay valid.`)) return;
    setVoiding(true);
    try {
      const res = await adminBatchVoidRemaining(id);
      toast.success(`${res.voided_count} codes voided.`);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Void failed.');
    } finally { setVoiding(false); }
  };

  const doDelete = async () => {
    if (!window.confirm('Delete this batch entirely? (Only works if no codes have been redeemed yet.)')) return;
    setDeleting(true);
    try {
      await adminDeletePartnerCodeBatch(id);
      toast.success('Batch deleted.');
      window.location.href = '/admin/partner-codes';
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed.');
      setDeleting(false);
    }
  };

  if (loading && !batch) return <div className="p-6 text-sm text-muted-sage">Loading…</div>;
  if (!batch) return <div className="p-6 text-sm text-muted-sage">Batch not found.</div>;

  const compClosed = ['SELECTING_WINNER', 'DRAWN'].includes((batch.competition_status || '').toUpperCase());

  return (
    <div data-testid="admin-partner-batch-detail">
      <Link to="/admin/partner-codes" className="inline-flex items-center gap-1.5 text-xs text-muted-sage hover:text-[color:var(--pd-text)] mb-4">
        <ArrowLeft size={13} /> All batches
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-widest">{batch.base_word}1 … {batch.base_word}{batch.end_at}</div>
          <h1 className="font-display text-3xl md:text-4xl leading-tight mt-1">{batch.label}</h1>
          <div className="text-xs text-muted-sage mt-1">Grants entry into <span className="text-[color:var(--pd-text)]">{batch.competition_title}</span> &middot; created {fmt(batch.created_at)}{batch.created_by ? ` by ${batch.created_by}` : ''}</div>
          {compClosed ? (
            <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded bg-[color:var(--pd-danger)]/15 text-[color:var(--pd-danger)]">
              <AlertTriangle size={11} /> Linked competition has closed — unused codes auto-expired
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={doVoidRemaining}
            disabled={voiding || !(counts.unused > 0)}
            className="inline-flex items-center gap-1.5 text-xs rounded-lg px-3 py-2 border border-[color:var(--pd-gold)]/50 text-[color:var(--pd-gold)] hover:bg-[color:var(--pd-gold)]/10 disabled:opacity-40"
            data-testid="partner-batch-void-remaining"
          >
            {voiding ? <Loader2 size={13} className="animate-spin" /> : <ShieldOff size={13} />}
            Void remaining
          </button>
          <button
            type="button"
            onClick={doDelete}
            disabled={deleting || counts.redeemed > 0}
            title={counts.redeemed > 0 ? 'Delete blocked — codes have been redeemed. Use "Void remaining" instead.' : ''}
            className="inline-flex items-center gap-1.5 text-xs rounded-lg px-3 py-2 border border-[color:var(--pd-danger)]/40 text-[color:var(--pd-danger)] hover:bg-[color:var(--pd-danger)]/10 disabled:opacity-40"
            data-testid="partner-batch-delete"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-6" data-testid="partner-batch-stats">
        <Stat label="Total" value={counts.total ?? batch.quantity} />
        <Stat label="Redeemed" value={counts.redeemed ?? 0} accent />
        <Stat label="Unused" value={counts.unused ?? 0} />
        <Stat label="Expired" value={counts.expired ?? 0} muted />
        <Stat label="Voided" value={counts.voided ?? 0} muted />
      </div>

      {/* Email composer */}
      <div className="bg-surface border border-pd rounded-2xl p-5 mb-6" data-testid="partner-batch-composer">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-[color:var(--pd-gold)]" />
            <div className="font-mono text-[10px] text-muted-sage uppercase tracking-widest">Email template</div>
          </div>
          <div className="text-[11px] text-muted-sage">Merge tokens: <span className="font-mono text-[color:var(--pd-text)]">{'{{code}}'}</span>, <span className="font-mono text-[color:var(--pd-text)]">{'{{label}}'}</span></div>
        </div>
        <label className="block mb-3">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mb-1 block">Subject</span>
          <input
            value={composer.subject}
            onChange={(e) => { setComposer((c) => ({ ...c, subject: e.target.value })); dirtyRef.current = true; }}
            className="w-full h-11 rounded-xl bg-[color:var(--pd-bg)] border border-pd px-3 text-sm text-[color:var(--pd-text)] focus:border-[color:var(--pd-accent-yellow)] outline-none"
            data-testid="partner-batch-subject"
          />
        </label>
        <label className="block mb-3">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mb-1 block">Body (plain text — blank lines become new paragraphs)</span>
          <textarea
            value={composer.body}
            onChange={(e) => { setComposer((c) => ({ ...c, body: e.target.value })); dirtyRef.current = true; }}
            rows={12}
            className="w-full rounded-xl bg-[color:var(--pd-bg)] border border-pd px-3 py-2.5 text-sm text-[color:var(--pd-text)] focus:border-[color:var(--pd-accent-yellow)] outline-none font-mono leading-relaxed"
            data-testid="partner-batch-body"
          />
        </label>
        <div className="flex flex-wrap gap-2 items-center">
          <button
            type="button" onClick={saveComposer} disabled={saving}
            className="inline-flex items-center gap-1.5 text-xs bg-[color:var(--pd-accent-yellow)] text-[#14251c] px-3 py-2 rounded-lg font-medium disabled:opacity-60"
            data-testid="partner-batch-save-template"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <SquareDashedBottomCode size={13} />}
            Save template
          </button>
          <input
            type="email" value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="Test to…"
            className="h-9 rounded-lg bg-[color:var(--pd-bg)] border border-pd px-2.5 text-xs text-[color:var(--pd-text)] focus:border-[color:var(--pd-accent-yellow)] outline-none max-w-[220px]"
            data-testid="partner-batch-test-email"
          />
          <button
            type="button" onClick={sendTest} disabled={testing}
            className="inline-flex items-center gap-1.5 text-xs border border-pd rounded-lg px-3 py-2 text-muted-sage hover:text-[color:var(--pd-text)] disabled:opacity-60"
            data-testid="partner-batch-send-test"
          >
            {testing ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            Send test to myself
          </button>
          <span className="text-[11px] text-muted-sage">Uses <span className="font-mono">SAMPLE123</span> in {'{{code}}'}.</span>
        </div>
      </div>

      {/* Assign & send */}
      <div className="bg-surface border border-pd rounded-2xl p-5 mb-6" data-testid="partner-batch-assigner">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-[color:var(--pd-gold)]" />
            <div className="font-mono text-[10px] text-muted-sage uppercase tracking-widest">Assign recipients &amp; send</div>
          </div>
          <div className="text-[11px] text-muted-sage"><span className="text-[color:var(--pd-text)]">{unassignedCount}</span> unassigned code{unassignedCount === 1 ? '' : 's'} available</div>
        </div>
        <textarea
          value={recipients}
          onChange={(e) => setRecipients(e.target.value)}
          rows={6}
          placeholder={"alice@example.com\nbob@example.com\ncarol@example.com"}
          className="w-full rounded-xl bg-[color:var(--pd-bg)] border border-pd px-3 py-2.5 text-sm text-[color:var(--pd-text)] focus:border-[color:var(--pd-accent-yellow)] outline-none font-mono leading-relaxed"
          data-testid="partner-batch-recipients"
        />
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-mono text-muted-sage">
          <span><span className="text-[color:var(--pd-accent-yellow)]">{parsedRecipients.valid.length}</span> valid</span>
          {parsedRecipients.invalid.length ? <span className="text-[color:var(--pd-danger)]">{parsedRecipients.invalid.length} invalid</span> : null}
          {parsedRecipients.duplicates.length ? <span>{parsedRecipients.duplicates.length} duplicate{parsedRecipients.duplicates.length === 1 ? '' : 's'}</span> : null}
        </div>
        {mismatchConfirm ? (
          <div className="mt-3 rounded-xl border border-[color:var(--pd-gold)]/60 bg-[color:var(--pd-gold)]/10 p-4 text-sm" data-testid="partner-batch-mismatch">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-[color:var(--pd-gold)] mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--pd-gold)] mb-1">Mismatch</div>
                <div className="text-[color:var(--pd-text)]">{mismatchConfirm.message}</div>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => doAssignAndSend(true)}
                    disabled={sending}
                    className="inline-flex items-center gap-1.5 text-xs bg-[color:var(--pd-accent-yellow)] text-[#14251c] px-3 py-2 rounded-lg font-medium"
                    data-testid="partner-batch-mismatch-confirm"
                  >
                    Send anyway
                  </button>
                  <button type="button" onClick={() => setMismatchConfirm(null)} className="text-xs border border-pd px-3 py-2 rounded-lg text-muted-sage hover:text-[color:var(--pd-text)]">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => doAssignAndSend(false)}
            disabled={sending || !parsedRecipients.valid.length}
            className="inline-flex items-center gap-1.5 text-sm bg-[color:var(--pd-accent-yellow)] text-[#14251c] px-4 py-2.5 rounded-xl font-medium disabled:opacity-60"
            data-testid="partner-batch-send-batch"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Send to {parsedRecipients.valid.length || 0} recipient{parsedRecipients.valid.length === 1 ? '' : 's'}
          </button>
        </div>
        {sendReport ? (
          <div className="mt-3 text-xs text-muted-sage" data-testid="partner-batch-send-report">
            <span className="text-[color:var(--pd-accent-yellow)]">{sendReport.sent_ok}</span> sent &middot; {sendReport.failed} failed{sendReport.unassigned_remaining ? ` · ${sendReport.unassigned_remaining} codes still unassigned` : ''}
          </div>
        ) : null}
      </div>

      {/* Codes table */}
      <div className="bg-surface border border-pd rounded-2xl overflow-hidden" data-testid="partner-batch-codes">
        <div className="flex items-center justify-between p-4 border-b border-pd flex-wrap gap-2">
          <div className="font-mono text-[10px] text-muted-sage uppercase tracking-widest">All codes</div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-8 rounded-lg bg-[color:var(--pd-bg)] border border-pd px-2 text-xs text-[color:var(--pd-text)] focus:border-[color:var(--pd-accent-yellow)] outline-none"
            data-testid="partner-batch-codes-filter"
          >
            <option value="">All statuses</option>
            <option value="unused">Unused</option>
            <option value="redeemed">Redeemed</option>
            <option value="expired">Expired</option>
            <option value="voided">Voided</option>
          </select>
        </div>
        {codes.length === 0 ? (
          <div className="p-5 text-sm text-muted-sage">No codes match this filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="font-mono text-[10px] uppercase tracking-widest text-muted-sage">
                <tr>
                  <Th>Code</Th>
                  <Th>Status</Th>
                  <Th>Sent to</Th>
                  <Th>Sent at</Th>
                  <Th>Redeemed by</Th>
                  <Th>Redeemed at</Th>
                  <Th>Ticket</Th>
                  <Th>&nbsp;</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--pd-border)]">
                {codes.map((c) => (
                  <tr key={c.id} className="align-top" data-testid="partner-batch-code-row">
                    <Td mono>{c.code}</Td>
                    <Td><CodeStatus status={c.status} /></Td>
                    <Td title={c.email_last_error || ''}>
                      {c.assigned_email || '—'}
                      {c.email_last_error ? (
                        <div className="text-[10px] text-[color:var(--pd-danger)] mt-0.5 truncate max-w-[220px]">{c.email_last_error}</div>
                      ) : null}
                    </Td>
                    <Td>{fmt(c.email_sent_at)}</Td>
                    <Td>{c.redeemed_by_user_email || '—'}</Td>
                    <Td>{fmt(c.redeemed_at)}</Td>
                    <Td mono>{c.ticket_number || '—'}</Td>
                    <Td>
                      {c.assigned_email && (!c.email_sent_at || c.email_last_error) ? (
                        <button
                          type="button" onClick={() => retry(c.id)} disabled={retryingId === c.id}
                          className="inline-flex items-center gap-1 text-[11px] text-[color:var(--pd-accent-yellow)] hover:underline disabled:opacity-60"
                          data-testid="partner-batch-retry"
                        >
                          {retryingId === c.id ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                          Retry
                        </button>
                      ) : null}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, accent, muted }) => (
  <div className={`rounded-xl border p-3 ${accent ? 'border-[color:var(--pd-accent-yellow)]/40 bg-[color:var(--pd-accent-yellow)]/10' : 'border-pd bg-surface'}`}>
    <div className="font-mono text-[9px] uppercase tracking-widest text-muted-sage">{label}</div>
    <div className={`font-display text-2xl mt-1 ${accent ? 'text-[color:var(--pd-accent-yellow)]' : muted ? 'text-muted-sage' : 'text-[color:var(--pd-text)]'}`}>{value}</div>
  </div>
);

const Th = ({ children }) => <th className="text-left font-normal px-3 py-2 whitespace-nowrap">{children}</th>;
const Td = ({ children, mono, ...rest }) => (
  <td className={`px-3 py-2 whitespace-nowrap ${mono ? 'font-mono text-[color:var(--pd-text)]' : 'text-[color:var(--pd-text)]'}`} {...rest}>{children}</td>
);

const CodeStatus = ({ status }) => {
  const s = (status || 'unused').toLowerCase();
  const map = {
    unused:   { icon: null,                     cls: 'text-muted-sage',                     label: 'Unused' },
    redeemed: { icon: <CheckCircle2 size={12}/>, cls: 'text-[color:var(--pd-accent-yellow)]', label: 'Redeemed' },
    expired:  { icon: <XCircle size={12}/>,      cls: 'text-[color:var(--pd-danger)]',        label: 'Expired' },
    voided:   { icon: <ShieldOff size={12}/>,    cls: 'text-muted-sage',                      label: 'Voided' },
  };
  const d = map[s] || map.unused;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest ${d.cls}`} data-testid="partner-batch-code-status">
      {d.icon}{d.label}
    </span>
  );
};

export default AdminPartnerBatchDetail;
